import {
  Component,
  ChangeDetectionStrategy,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';

import {
  REQUEST_FULLSCREEN,
  SHOW_FEEDBACK_BUTTON,
  SHOW_BACK_TO_REVIEW_IN_REGULAR_MODE,
  TRAINING_MODE,
  FEEDBACK_URL,
  QUESTIONARIO_URL,
  WELCOME_PAGE
} from '../../constants';
import { Translate, Utils } from '../../services';
import {
  AssessmentSelectors,
  AssessmentService,
  ViewService
} from '../../logics';

@Component({
  selector: 'report-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <cs-user-bar 
      [inputs]="{
          user: shared.userData,
          trainingMode: trainingMode,
          feedbackUrl: feedbackUrl
      }"
      (highContrastToggle)="highContrastToggle.emit()"
      (expandImage)="expandImage.emit($event)"
      (increaseZoom)="increaseZoom.emit()"
      (decreaseZoom)="decreaseZoom.emit()">
    </cs-user-bar>

    <cs-page-title [inputs]="{
      title: 'Risultati test', 
      isReport: true
    }">
    </cs-page-title>

    <div class="cs-navigator">
      <a 
        class="cs-navigator__review-button cs-btn cs-btn-l cs-btn-light"
        (click)="viewChange.emit('survey')"
        *ngIf="!(hideBackToReview$ | async)">
        {{ (translations$ | async)['report#back_to_survey_1'] }} 
        {{ (translations$ | async)['report#back_to_survey_2'] }}
      </a>
      <a
          *ngIf="showfeedbackButton"
          class="cs-navigator__exit cs-btn cs-btn-l cs-btn-light cs-navigator__external"
          href="{{
          questionarioUrl +
          '?info=' + shared.userData?.userId + 
          '-' + shared.userData?.testId +
          trainingInfo + (trainingMode ? '1' : '0')
        }}"
          target="_blank"
          [style.cursor]="'alias'">
        Lasciaci una tua impressione <span class="ion-android-open"></span>
      </a>
      <a 
        class="cs-navigator__exit cs-btn cs-btn-l cs-btn-light"
        (click)="testExit()">
        Esci <span class="ion-close-round"></span>
      </a>
    </div>

    <div class="cs-final-report-page">
      <cs-section-result 
        *ngFor="let section of preTotalSections" 
        [inputs]="section">
      </cs-section-result>
      <div class="cs-final-report-page__report-value">
        <p>
          {{ (translations$ | async)['report#point_label'] }}
          <strong>{{ totalPoints }}</strong>
        </p>
      </div>
      <cs-section-result
          *ngFor="let section of postTotalSections"
          [inputs]="section">
      </cs-section-result>
      <div class="end-test-link-wrapper" *ngIf="linkFineTestUrl !== false">
        <a [href]="linkFineTestUrl">{{linkFineTestText}}</a>
      </div>
    </div>
  `
})

export class ReportComponent implements OnInit, OnDestroy {
  trainingInfo: string = '&a=';
  feedbackUrl: string = FEEDBACK_URL;
  questionarioUrl: string = QUESTIONARIO_URL;
  trainingMode: boolean = TRAINING_MODE;
  showBackToReviewInRegularMode: boolean = SHOW_BACK_TO_REVIEW_IN_REGULAR_MODE;
  showfeedbackButton: boolean = SHOW_FEEDBACK_BUTTON;
  requestFullscreen: boolean;

  reportData$: Observable<any>;
  reportDataFiltered$: Observable<any>;
  totalScore$: Observable<number>;
  hideBackToReview$: Observable<boolean>;
  destroyed$: Subject<any> = new Subject<any>();
  translations$: Observable<any>;

  @Input() shared: Object;
  @Output() viewChange = new EventEmitter<string>();
  @Output() highContrastToggle = new EventEmitter<Object>();
  @Output() increaseZoom = new EventEmitter<Object>();
  @Output() decreaseZoom = new EventEmitter<Object>();
  @Output() expandImage = new EventEmitter<Object>();

  preTotalSections = [];
  postTotalSections = [];
  totalPoints = 0;

  linkFineTestUrl: any = false;
  linkFineTestText: any = false;

  constructor(
    private assessmentService: AssessmentService,
    private assessmentSelector: AssessmentSelectors,
    private viewService: ViewService,
    private translate: Translate,
    private utils: Utils
  ) {
    this.translations$ = this.translate.getTranslations$();
    this.reportData$ = this.assessmentService.getResults$();

    this.requestFullscreen = REQUEST_FULLSCREEN && this.utils.isWeb();

    this.reportData$.subscribe( (sections) => {
      let i;
      for (i = 0; i < Object.keys(sections).length; i ++) {
        if (sections[i].label.toLowerCase() === 'brano inglese' || sections[i].label.toLowerCase() === 'inglese') {
          this.postTotalSections.push(sections[i]);
        }
        else {
          this.totalPoints += sections[i].sectionPoints;
          this.preTotalSections.push(sections[i]);
        }
      }
    });

    /*
    this.totalScore$ = this.reportData$
      .map(sections =>
        Object.keys(sections)
          .reduce((acc, i) => acc + (sections[i].sectionPoints), 0));
    */

    this.hideBackToReview$ = this.reportData$
      .withLatestFrom(this.assessmentService.getRicetteDetails$())
      .withLatestFrom(this.assessmentService.getMetadata$())
      .map(([[sections, ricette], metadata]) => {
        if (!this.trainingMode && !this.showBackToReviewInRegularMode) {
          return true;
        }

        if (typeof metadata.salvaRisultati !== 'undefined' && metadata.salvaRisultati) {
          return true;
        }

        let sectionToHideCount = 0;

        for (let i in sections) {
          if (sections.hasOwnProperty(i) &&
            ricette[sections[i].ricettaId].hide) {
            sectionToHideCount++;
          }
        }

        return sectionToHideCount === sections.length;
      });
  }

  ngOnInit() {
    this.viewService.isFullscreen$()
      .filter(() => this.requestFullscreen)
      .takeUntil(this.destroyed$)
      .subscribe(isFullscreen => {
        if (!isFullscreen) {
          this.viewChange.emit('wait');
        }
      });
    this.assessmentSelector.getAssessmentLinkFineTest()
      .takeUntil(this.destroyed$)
      .subscribe((linkFineTest) => {
      if (linkFineTest !== null && typeof linkFineTest === 'object') {
        this.linkFineTestText = linkFineTest.text;
        this.linkFineTestUrl = linkFineTest.url;
      }
    });
  }

  testExit() {
    if (!this.trainingMode) {
      // fix tablet
      let welcomePage = WELCOME_PAGE;
      if(this.utils.isMobile() || this.utils.isDesktop()){
        welcomePage = 'welcome_local.html';
      }
      location.href = welcomePage;
    }
    else {
      if (typeof this.shared['metaData'].tipoTest !== 'undefined' && typeof this.shared['metaData'].salvaRisultati !== 'undefined') {
        if (this.shared['metaData'].tipoTest === 'posizionamento' && this.shared['metaData'].salvaRisultati) {
          setTimeout(() => {
            window.location.href = '/utenti_esterni/posizionamento.php';
          }, 1000);
          return;
        }
      }
      location.reload();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }
}



// WEBPACK FOOTER //
// ./src/app/layout/report/report.component.ts